import { useEffect, useRef, useState } from "react";
import BlankLayout from "./BlankLayout";
import {
  IMAGE_STATUS,
  getSlideImageEntry,
  imageKeyOf,
  preloadSlideImage,
  useSlideImageVersion,
} from "./slideImageCache";
import "./ImageLayout.css";

// Card #2485 (Scoreboard 2.0, docs/score/scoreboard-2.0.md "Image mode and
// mode groups", mockup demo/scoreboard/2.0/image-mode.html) -- one configured
// picture, with an optional caption beneath it, on the fixed design canvas.
//
// The picture is CONTAINED (ImageLayout.css: object-fit: contain), never
// cropped and never stretched, whatever aspect ratio the source has against
// the board's.
//
// A broken, expired or blocked image NEVER shows the browser's broken-image
// icon and never an error (#1798: "NEVER show an error message on the
// scoreboard"): after the recovery below fails, this component renders the
// same BlankLayout every other "nothing to show" case renders, with the same
// theme-aware logo fallback.
//
// URL stability (#2321 / #2327). The presigned URL is NOT byte-identical
// across polls: presignAssetUrl's cache is per Lambda instance, so polls that
// land on different warm instances carry different signatures for the SAME S3
// key. Handing each one to <img src> would re-request the picture every poll
// and flicker. So, like ScoreboardFrame's useAssetUrlStabilizer, the src is
// held while the asset's S3 key (the URL path, query string ignored) is
// unchanged, and a changed key switches immediately.
//
// Unlike that hook, the pinned URL is not trusted forever (card #2327): if
// the <img> errors (the pinned signature expired, or a transient failure),
// the newest polled URL for the same key is adopted and tried once. Only when
// that retry also fails, or there is no URL at all, does the board fall back
// to Blank/logo.
//
// Recovery from the fallback (card #3081). Falling back is not permanent: a
// short network or S3 blip can fail the immediate retry too, and the key (the
// only thing that cleared the fallback) may never change, so the wall would
// stay Blank until someone reloaded it. While failed, the board keeps showing
// Blank/logo and schedules another attempt with the NEWEST polled URL on a
// capped backoff (30s, 60s, 120s, 240s, then 5 min). The backoff resets on a
// successful load or a key change, and the timer is cleared on unmount.
// Rejected alternative: retry only when a poll delivers a new signature, since
// signatures can stay identical inside one Lambda's presign cache and it might
// never retry.
//
// Never half-painted (card #3311). The <img> is in the DOM from the start but
// invisible (ImageLayout.css) until slideImageCache reports the picture fully
// downloaded AND decoded, so it can never be seen painting top to bottom. The
// src handed to <img> is the URL the cache pinned for this S3 key, so a picture
// shown again, or preloaded as the "next slide", is a browser-cache hit. A load
// failure seen by the cache and an <img> error are the same failure and feed the
// same retry budget below: a retry restarts the download, which discards the
// failed attempt, so one failure is never counted twice.
export const RETRY_BASE_MS = 30 * 1000;
export const RETRY_CAP_MS = 5 * 60 * 1000;
export function retryDelayMs(attempt) {
  return Math.min(RETRY_BASE_MS * 2 ** attempt, RETRY_CAP_MS);
}

function s3KeyOf(url) {
  if (!url) return url;
  const queryIndex = url.indexOf("?");
  return queryIndex === -1 ? url : url.slice(0, queryIndex);
}

// Card #3314 -- `slide` marks an Image Loop slide: no padding or caption, the
// picture always full height of its space, centered, and cropped equally left
// and right when wider than the space (ImageLayout.css). The single Image mode
// (slide false) is unchanged.
export default function ImageLayout({ imageAssetUrl, caption, logoAssetUrl, theme, slide = false }) {
  const key = s3KeyOf(imageAssetUrl);
  // src actually handed to <img>; retried: the one allowed retry for this key
  // has been spent; failed: the retry also failed (reset when the key changes).
  const [state, setState] = useState({ key, src: imageAssetUrl, retried: false, failed: false, attempt: 0 });
  const latestUrlRef = useRef(imageAssetUrl);
  latestUrlRef.current = imageAssetUrl;
  const reportErrorRef = useRef(() => {});
  // The attempt whose failure was already reported: the cache and the <img>
  // both see the same failure, and one failure must spend the retry budget once.
  const erroredAttemptRef = useRef(null);

  // A changed key (a different asset) switches immediately and resets the
  // retry budget. Derived during render so there is no stale-src frame.
  let current = state;
  if (state.key !== key) {
    current = { key, src: imageAssetUrl, retried: false, failed: false, attempt: 0 };
    setState(current);
  }

  // Failed: stay on Blank/logo but try again later with the newest polled URL.
  // The deps restart the timer on a new attempt and clear it on unmount, on a
  // key change and on recovery.
  const { failed, attempt } = current;
  useEffect(() => {
    if (!failed) return undefined;
    const timer = setTimeout(() => {
      setState((prev) => {
        if (prev.key !== key || !prev.failed) return prev;
        return {
          ...prev,
          src: latestUrlRef.current || prev.src,
          failed: false,
          // The single immediate retry is already spent: one more error goes
          // straight back to Blank/logo and a longer wait.
          retried: true,
          retryNonce: (prev.retryNonce || 0) + 1,
          attempt: prev.attempt + 1,
        };
      });
    }, retryDelayMs(attempt));
    return () => clearTimeout(timer);
  }, [failed, attempt, key]);

  // Card #3311: download + decode off-screen. Re-runs on every cache change
  // (cheap, idempotent) so a failure the cache records reaches handleError.
  useSlideImageVersion();
  const attemptId = `${key}#${current.retryNonce || 0}`;
  const forcedAttemptRef = useRef(null);
  useEffect(() => {
    if (!imageAssetUrl || current.failed) return;
    // A retry (nonce > 0) restarts the download once for that attempt.
    const force = (current.retryNonce || 0) > 0 && forcedAttemptRef.current !== attemptId;
    forcedAttemptRef.current = attemptId;
    preloadSlideImage(current.src, { force });
    if (getSlideImageEntry(current.src)?.status === IMAGE_STATUS.FAILED) reportErrorRef.current();
  });

  // Same key, new signature: keep current.src (no DOM change). The newest URL
  // is read from latestUrlRef only on error.
  if (!imageAssetUrl || current.failed) {
    return <BlankLayout logoAssetUrl={logoAssetUrl} theme={theme} />;
  }

  const handleError = () => {
    if (erroredAttemptRef.current === attemptId) return;
    erroredAttemptRef.current = attemptId;
    const newest = latestUrlRef.current;
    setState((prev) => {
      if (!prev.retried) {
        // Adopt the newest polled URL (possibly the same string) and remount
        // the <img> (retryNonce in its React key) so it is requested again.
        return { ...prev, src: newest || prev.src, retried: true, retryNonce: (prev.retryNonce || 0) + 1 };
      }
      return { ...prev, failed: true };
    });
  };

  // A successful load restores the full retry budget and resets the backoff.
  const handleLoad = () => {
    setState((prev) => (prev.attempt === 0 && !prev.retried ? prev : { ...prev, attempt: 0, retried: false }));
  };

  reportErrorRef.current = handleError;

  // The URL the cache pinned for this key (loading or ready), so the browser
  // reuses what was preloaded; otherwise the src this component pinned.
  const entry = getSlideImageEntry(current.src);
  const imgSrc = entry && entry.status !== IMAGE_STATUS.FAILED ? entry.url : current.src;
  const isReady = Boolean(entry) && entry.status === IMAGE_STATUS.READY && imageKeyOf(entry.url) === key;

  const captionText = !slide && typeof caption === "string" ? caption.trim() : "";
  return (
    <div className={slide ? "scoreboard-image scoreboard-image--slide" : "scoreboard-image"} data-testid="scoreboard-image">
      {/* alt="" -- decorative on a board nobody reads with a screen reader. */}
      <img
        key={`${imgSrc}#${current.retryNonce || 0}`}
        className={isReady ? "scoreboard-image__img scoreboard-image__img--ready" : "scoreboard-image__img"}
        src={imgSrc}
        alt=""
        data-testid="scoreboard-image-img"
        onError={handleError}
        onLoad={handleLoad}
      />
      {captionText ? (
        <div className="scoreboard-image__caption" data-testid="scoreboard-image-caption">
          {captionText}
        </div>
      ) : null}
    </div>
  );
}
